import { useRouter } from "next/router";
import { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import {
  useActiveAccount,
  useReadContract,
  MediaRenderer,
} from "thirdweb/react";
import TransactionButton from "../../components/Common/TxButton";
import { client } from "../../utils/constants";
import { NFTDropContractMars } from "../../utils/contract/mars";
import { NFTDropContractMoon } from "../../utils/contract/moon";
import {
  claimTo,
  getTotalClaimedSupply,
  getTotalUnclaimedSupply,
  getOwnedNFTs,
} from "thirdweb/extensions/erc721";
import { toast } from "react-toastify";
import scroll2 from "../../images/scroll-2.gif";
import {
  MdFirstPage,
  MdLastPage,
  MdChevronRight,
  MdChevronLeft,
  MdClose,
} from "react-icons/md";
import { getSupabase } from "../../utils/supabase/client";

export const sData = [
  { links: "mars", link: "mars", title: "Mars", count: "1,885 regions", price: 1, category: "Planets", id: "mars", glb: "https://moon.niftysky.org/mars" },
  { links: "moon", link: "moon", title: "Moon", count: "10,362 regions", price: 0.5, category: "Moons", id: "earthsmoon", glb: "https://moon.niftysky.org/earthsmoon" },
  { links: "Venus", link: "venus", title: "Venus", count: "1976 regions", category: "Planets", price: 0.3, id: "venus", glb: "https://moon.niftysky.org/venus" },
  { links: "neo", link: "neo", title: "Near Earth Asteroids Registry", count: "2970 asteroids", price: 0.25, category: "Asteroids", id: "eros", glb: "https://moon.niftysky.org/eros" },
  { links: "mercury", link: "mercury", title: "Mercury", count: "539 regions", price: 0.4, category: "Planets", id: "mercury", glb: "https://planets.niftysky.org/mercury" },
  { links: "Titan", link: "titan", title: "Titan", price: 0.5, count: "284 regions", category: "Moons", id: "titan", glb: "https://planets.niftysky.org/titan" },
  { links: "Pluto", link: "pluto", title: "Pluto", count: "52 regions", price: 0.7, category: "Planets", id: "pluto", glb: "https://planets.niftysky.org/pluto" },
  { links: "Ganymede", link: "ganymede", title: "Ganymede", count: "196 regions", price: 0.25, category: "Moons", id: "ganymede", glb: "https://planets.niftysky.org/ganymede" },
  { links: "Europa", link: "europa", title: "Europa", count: "121 regions", price: 0.6, category: "Moons", id: "europa", glb: "https://planets.niftysky.org/europa" },
  { links: "exoplanets", link: "exoplanets", title: "Exoplanets", count: "4947 regions", price: 0.5, category: "Planets", id: "cancri", glb: "https://planets.niftysky.org/cancri" },
  { links: "pha", link: "pha", title: "Potentially Hazardous Asteroids Registry", count: "772 regions", price: 0.3, category: "Asteroids", id: "bennu", glb: "https://planets.niftysky.org/bennu" },
  { links: "rhea", link: "rhea", title: "Rhea", count: "143 regions", price: 0.25, migrated: false, category: "Moons", id: "rhea", glb: "https://planets.niftysky.org/rhea" },
  { links: "callisto", link: "callisto", title: "Callisto", count: "154 regions", migrated: true, price: 0.25, category: "Moons", id: "callisto", glb: "https://planets.niftysky.org/callisto" },
  { links: "io", link: "io", title: "Io", count: "224 regions", migrated: true, price: 0.25, category: "Moons", id: "io", glb: "https://planets.niftysky.org/io" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500];

// ─── Supabase tables ───────────────────────────────────────────────────────
// Every body now reads from Supabase: planetary regions from
// niftysky_planetary_features, asteroids (NEO / PHA) from niftysky_asteroids.
const TABLE = "niftysky_planetary_features";
const ASTEROID_TABLE = "niftysky_asteroids";

const LIST_COLUMNS =
  "id, niftysky_id, title, clean_name, feature_type, diameter, center_latitude, center_longitude, wikipedia_url, image, approval_status, approval_date, feature_id";

// Full per-feature detail, loaded lazily when a row is expanded (only meaningful
// for USGS-scraped bodies — wkt_string / iframe_url are null for the others).
const DETAIL_COLUMNS =
  "iframe_url, wkt_string, origin, clean_name, target, coordinate_system, " +
  "diameter_scraped, center_latitude_scraped, center_longitude_scraped, " +
  "northmost_latitude, southmost_latitude, eastmost_longitude, westmost_longitude, " +
  "quad_scraped, continent, ethnicity, reference, description, wiki_description, " +
  "stellarium_url, ipfs_url";

const ASTEROID_LIST_COLUMNS =
  "id, niftysky_id, title, category, link, a, e, i, ad, h, condition_code";

// Round a numeric value to 2 decimals (or null).
const round2 = (val) => {
  if (val == null) return null;
  const n = Number(val);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

// Normalize a Supabase planetary row into the shape RegionRow expects.
const mapPlanetaryFeature = (row) => ({
  isSupabase: true,
  id: row.id,
  title: row.title || row.clean_name || "Unnamed feature",
  feature_type: row.feature_type || "—",
  diameter: round2(row.diameter),
  center_latitude: round2(row.center_latitude),
  center_longitude: round2(row.center_longitude),
  wikipedia: row.wikipedia_url || null,
  image: row.image || null,
  approval_status: row.approval_status || null,
  approval_date: row.approval_date || null,
  feature_id: row.feature_id || null,
});

// Normalize a Supabase asteroid row.
const mapAsteroid = (row) => ({
  isSupabase: true,
  isAsteroid: true,
  id: row.id,
  title: row.title || row.niftysky_id || "Unnamed",
  a: row.a,
  e: row.e,
  i: row.i,
  ad: row.ad,
  h: row.h,
  condition_code: row.condition_code,
  wikipedia: row.link || null,
});

// Fetch every row matching a filter — chunked at 1000 rows (Supabase max).
async function fetchAllRows(table, columns, applyFilter) {
  const supabase = getSupabase();
  const CHUNK = 1000;
  const query = (withCount) => {
    let q = supabase.from(table).select(columns, withCount ? { count: "exact" } : undefined);
    q = applyFilter(q);
    return q.order("title", { nullsFirst: false }).order("id");
  };

  const first = await query(true).range(0, CHUNK - 1);
  if (first.error) throw first.error;
  let rows = first.data || [];
  const total = first.count ?? rows.length;

  const requests = [];
  for (let start = CHUNK; start < total; start += CHUNK) {
    requests.push(query(false).range(start, start + CHUNK - 1));
  }
  const rest = await Promise.all(requests);
  for (const r of rest) {
    if (r.error) throw r.error;
    rows = rows.concat(r.data || []);
  }
  return rows;
}

// ─── CartoCosmos map ────────────────────────────────────────────────────────
// The ASC CartoCosmos viewer exposes a postMessage API: sending
// `"drawWkt <wkt>"` makes it draw that polygon on the real planetary map AND
// zoom/fit to it — i.e. the feature boundary appears at its exact geographic
// position, exactly as on planetarynames.wr.usgs.gov. The viewer registers its
// message listener only after its SPA boots, so the command is retried for a
// few seconds (re-drawing is idempotent — it just re-fits to the same bounds).
function CartoMap({ src, wkt, title, className }) {
  const frameRef = useRef(null);

  useEffect(() => {
    const iframe = frameRef.current;
    if (!iframe || !src || !wkt) return;
    let attempts = 0;
    let timer;
    const send = () => {
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage("drawWkt " + wkt, "*");
        }
      } catch (e) {
        /* cross-origin guard — ignore */
      }
      attempts += 1;
      if (attempts < 9) timer = setTimeout(send, 700);
    };
    timer = setTimeout(send, 900); // let the CartoCosmos SPA boot first
    return () => clearTimeout(timer);
  }, [src, wkt]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      className={className}
      loading="lazy"
    />
  );
}

// ─── Filter Chip ────────────────────────────────────────────────────────────
function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-200 text-xs font-medium transition-all hover:bg-sky-500/25">
      {label}
      <button
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-white/10 text-sky-300 hover:text-white transition-colors"
      >
        <MdClose className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

// ─── Stat tile (inside the expanded panel) ──────────────────────────────────
function StatTile({ label, value }) {
  const empty = value === null || value === undefined || value === "" || value === "—";
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.12em] text-gray-500">{label}</div>
      <div className={`mt-0.5 text-sm font-medium break-words ${empty ? "text-gray-600" : "text-white"}`}>
        {empty ? "—" : value}
      </div>
    </div>
  );
}

// Labeled section inside the expanded panel.
function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-400/80 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function ExtLinkIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

// ─── Expandable Region Row ──────────────────────────────────────────────────
function RegionRow({ item, staticData, isAsteroid }) {
  const [expanded, setExpanded] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [detail, setDetail] = useState(null); // { iframe_url, wkt_string, origin }
  const [detailLoading, setDetailLoading] = useState(false);
  const isSupabase = !!item.isSupabase;

  // Lazily load the full per-feature detail the first time the row is opened.
  // NOTE: `detailLoading` is intentionally NOT a dependency. If it were, the
  // setDetailLoading(true) call would re-run this effect, and the re-run's
  // cleanup would flip `cancelled` true — so the request's .then() would bail
  // out and the map would spin forever.
  useEffect(() => {
    if (!isSupabase) return;
    if (isAsteroid) return; // asteroids don't have planetary detail / map
    if (!(expanded || showImageModal)) return;
    if (detail) return;

    let cancelled = false;
    setDetailLoading(true);
    getSupabase()
      .from(TABLE)
      .select(DETAIL_COLUMNS)
      .eq("id", item.id)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error("Feature detail fetch failed:", error);
        setDetail(data || {});
        setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSupabase, isAsteroid, expanded, showImageModal, detail, item.id]);

  const mapUrl = isSupabase ? detail?.iframe_url : null;

  return (
    <>
      {showImageModal && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-lg flex items-center justify-center p-3"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="relative bg-gray-900/95 rounded-2xl border border-blue-800/30 shadow-2xl flex flex-col overflow-hidden"
            style={{ width: "90vw", height: "80vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 shrink-0">
              <h3 className="text-white font-semibold text-sm sm:text-base truncate">{item.title}</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="shrink-0 p-1.5 rounded-full bg-black/60 text-white hover:bg-white/10 transition-colors"
                aria-label="Close map"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="relative flex-1 bg-black">
              {detailLoading ? (
                <div className="absolute inset-0 flex items-center justify-center gap-3 text-gray-400">
                  <div className="w-5 h-5 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                  Loading interactive map…
                </div>
              ) : mapUrl ? (
                <CartoMap
                  src={mapUrl}
                  wkt={detail?.wkt_string}
                  title={`${item.title} interactive map`}
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : item.image ? (
                <img
                  src={item.image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-contain"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
                  No map available for this feature.
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div
        className={`group border rounded-xl mb-1.5 transition-all duration-300 ${
          expanded
            ? "bg-blue-950/25 border-blue-800/40"
            : "border-transparent hover:bg-white/[0.03] hover:border-white/5"
        }`}
      >
        <div
          className="grid items-center gap-3 px-4 py-3 cursor-pointer"
          style={{ gridTemplateColumns: "36px 1.5fr 1fr 1fr 1fr 1fr 1fr" }}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex justify-center">
            <div
              className={`w-6 h-6 rounded-md flex items-center justify-center border transition-all duration-300 ${
                expanded
                  ? "bg-sky-500/20 border-sky-500/40 text-sky-300"
                  : "bg-white/5 border-white/10 text-gray-400 group-hover:border-sky-500/30 group-hover:text-blue-400"
              }`}
            >
              <MdChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${expanded ? "rotate-90" : ""}`} />
            </div>
          </div>

          {isAsteroid ? (
            <>
              <div className="text-sky-200 font-medium text-sm truncate">
                {item.wikipedia ? (
                  <a href={item.wikipedia} target="_blank" rel="noopener noreferrer" className="hover:text-sky-300 hover:underline" onClick={(e) => e.stopPropagation()}>
                    {item.title}
                  </a>
                ) : (
                  <span>{item.title}</span>
                )}
              </div>
              <div className="text-gray-300 text-sm text-center">{item.ad}</div>
              <div className="text-gray-300 text-sm text-center">
                <span className="inline-flex px-2 py-0.5 rounded-md bg-white/5 text-xs">{item.condition_code}</span>
              </div>
              <div className="text-gray-300 text-sm text-center">{item.e}</div>
              <div className="text-gray-300 text-sm text-center">{item.h}</div>
              <div className="text-gray-300 text-sm text-center">{item.i}</div>
            </>
          ) : (
            <>
              <div className="text-sky-200 font-medium text-sm truncate group-hover:text-sky-300 transition-colors">
                {item.title}
              </div>
              <div className="text-center">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowImageModal(true); }}
                  className="text-xs px-3 py-1 rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-300 hover:bg-sky-500/20 hover:border-blue-400/40 transition-all duration-200"
                >
                  View Map
                </button>
              </div>
              <div className="text-gray-300 text-sm text-center tabular-nums">{item.diameter ?? "—"}</div>
              <div className="text-gray-300 text-sm text-center tabular-nums">{item.center_latitude ?? "—"}</div>
              <div className="text-gray-300 text-sm text-center tabular-nums">{item.center_longitude ?? "—"}</div>
              <div className="text-gray-300 text-sm text-center">
                <span className="inline-flex px-2 py-0.5 rounded-md bg-white/5 text-xs truncate max-w-full">{item.feature_type}</span>
              </div>
            </>
          )}
        </div>

        {expanded && !isAsteroid && (
          <div className="px-3 sm:px-4 pb-4">
            <div className="mt-1 rounded-2xl bg-black/40 border border-white/10 overflow-hidden">
              {/* ── Map — full width ──────────────────────────────── */}
              <div className="border-b border-white/10">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]" />
                    <span className="text-[11px] font-semibold text-sky-200 uppercase tracking-[0.16em]">
                      {isSupabase ? "Interactive Map" : "Region Map"}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowImageModal(true)}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                    Fullscreen
                  </button>
                </div>
                <div className="relative bg-black h-[300px] sm:h-[380px] lg:h-[460px]">
                  {detailLoading ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-400 bg-black/40">
                      <div className="w-6 h-6 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                      <span className="text-xs">Loading interactive map…</span>
                    </div>
                  ) : mapUrl ? (
                    <CartoMap
                      src={mapUrl}
                      wkt={detail?.wkt_string}
                      title={`${item.title} map`}
                      className="absolute inset-0 w-full h-full border-0"
                    />
                  ) : item.image ? (
                    <div
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `url(${item.image})`,
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
                      No map available
                    </div>
                  )}
                </div>
              </div>

              {/* ── Detail data ──────────────────────────────────── */}
              <div className="p-5 space-y-5">
                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.16em] text-sky-400/80 font-semibold mb-0.5">
                      {staticData?.title} Land Deed
                    </p>
                    <h4 className="text-xl font-bold text-white">{item?.title}</h4>
                  </div>
                  {isSupabase && item.feature_id && (
                    <span className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-gray-400 whitespace-nowrap">
                      USGS ID&nbsp;{item.feature_id}
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-400 leading-relaxed">
                  {staticData?.link === "moon"
                    ? `This NFT represents the real estate deed for ${item.title} Region of the Moon — capturing the region's map, geometry, naming and location data sourced from IAU, USGS and NASA.`
                    : `This NFT represents the real estate deed for ${item?.title} Region of Mars — capturing the region's map, geometry, naming and location data sourced from IAU, USGS and NASA.`}
                </p>

                {/* Coordinates & dimensions */}
                <Section title="Coordinates & Dimensions">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                    <StatTile label="Feature Type" value={item.feature_type} />
                    <StatTile
                      label="Diameter"
                      value={isSupabase ? detail?.diameter_scraped : (item.diameter != null ? `${item.diameter} km` : null)}
                    />
                    <StatTile
                      label="Center Latitude"
                      value={isSupabase ? detail?.center_latitude_scraped : item.center_latitude}
                    />
                    <StatTile
                      label="Center Longitude"
                      value={isSupabase ? detail?.center_longitude_scraped : item.center_longitude}
                    />
                    {isSupabase && (
                      <>
                        <StatTile label="North Latitude" value={detail?.northmost_latitude} />
                        <StatTile label="South Latitude" value={detail?.southmost_latitude} />
                        <StatTile label="East Longitude" value={detail?.eastmost_longitude} />
                        <StatTile label="West Longitude" value={detail?.westmost_longitude} />
                        <StatTile label="Coordinate System" value={detail?.coordinate_system} />
                        <StatTile label="Quad" value={detail?.quad_scraped} />
                        <StatTile label="Target Body" value={detail?.target || staticData?.title} />
                        <StatTile label="USGS Feature ID" value={item.feature_id} />
                      </>
                    )}
                  </div>
                </Section>

                {/* Designation & status */}
                {isSupabase && (
                  <Section title="Designation & Status">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      <StatTile label="Approval Status" value={item.approval_status} />
                      <StatTile label="Approval Date" value={item.approval_date} />
                      <StatTile label="Continent / Region" value={detail?.continent} />
                      <StatTile label="Ethnicity / Culture" value={detail?.ethnicity} />
                    </div>
                  </Section>
                )}

                {/* Etymology */}
                {isSupabase && (
                  <Section title="Etymology / Origin">
                    {detailLoading ? (
                      <div className="space-y-1.5">
                        <div className="h-3 rounded bg-white/5 animate-pulse" />
                        <div className="h-3 w-4/5 rounded bg-white/5 animate-pulse" />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {detail?.origin || "No recorded etymology for this feature."}
                      </p>
                    )}
                  </Section>
                )}

                {/* Description */}
                {isSupabase && !detailLoading && (detail?.description || detail?.wiki_description) && (
                  <Section title="Description">
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {detail?.description || detail?.wiki_description}
                    </p>
                  </Section>
                )}

                {/* Reference */}
                {isSupabase && !detailLoading && detail?.reference && (
                  <Section title="Reference">
                    <p className="text-xs text-gray-500 leading-relaxed break-words">
                      {detail.reference}
                    </p>
                  </Section>
                )}

                {/* Links */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {item.wikipedia && (
                    <a
                      href={item.wikipedia}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-300 hover:bg-sky-500/20 transition-all"
                    >
                      <ExtLinkIcon />
                      USGS Planetary Nomenclature
                    </a>
                  )}
                  {detail?.stellarium_url && (
                    <a
                      href={detail.stellarium_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                    >
                      <ExtLinkIcon />
                      Reference Map
                    </a>
                  )}
                  {detail?.ipfs_url && (
                    <a
                      href={detail.ipfs_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                    >
                      <ExtLinkIcon />
                      IPFS Image
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function Category() {
  const router = useRouter();
  const { id } = router.query;

  const [data, setData] = useState(null); // ALL rows for the current body
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [staticData, setStaticData] = useState(null);
  const [categoryModel, setCategoryModel] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [mintedNFT, setMintedNFT] = useState(null);
  const [showMintModal, setShowMintModal] = useState(false);
  const [isFetchingMint, setIsFetchingMint] = useState(false);

  // Filter state
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [diameterMin, setDiameterMin] = useState("");
  const [diameterMax, setDiameterMax] = useState("");

  // Load EVERY row for the current body from Supabase (planetary OR asteroid).
  const loadAll = async (obj) => {
    setLoading(true);
    setData(null);
    try {
      if (obj.category === "Asteroids") {
        const rows = await fetchAllRows(ASTEROID_TABLE, ASTEROID_LIST_COLUMNS, (q) =>
          q.eq("category", obj.links).eq("is_active", true)
        );
        setData(rows.map(mapAsteroid));
      } else {
        const rows = await fetchAllRows(TABLE, LIST_COLUMNS, (q) =>
          q.eq("category", obj.links).eq("is_active", true).is("scrape_error", null)
        );
        setData(rows.map(mapPlanetaryFeature));
      }
    } catch (err) {
      console.error("Supabase load failed:", err);
      toast.error("Failed to load registry data");
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!id) return;
    const obj = sData.find((item) => item.link === id);
    if (!obj) return;
    setStaticData(obj);
    setCategoryModel(obj.category);
    setPage(1);
    setSearchText("");
    setSelectedFeatures([]);
    setDiameterMin("");
    setDiameterMax("");
    loadAll(obj);
  }, [id]);

  const account = useActiveAccount();
  const { data: totalClaimedNFTs } = useReadContract(getTotalClaimedSupply, {
    contract: id === "moon" ? NFTDropContractMoon : NFTDropContractMars,
  });

  const isAsteroid = categoryModel === "Asteroids";

  // Unique feature types across the whole dataset → filter chips.
  const availableFeatures = useMemo(() => {
    if (!data || isAsteroid) return [];
    const set = new Set();
    data.forEach((item) => {
      if (item.feature_type && item.feature_type !== "—") set.add(item.feature_type);
    });
    return Array.from(set).sort();
  }, [data, isAsteroid]);

  // Apply search + feature + diameter filters across the dataset.
  const filterData = useMemo(() => {
    if (!data) return null;
    const q = searchText.trim().toLowerCase();
    return data.filter((item) => {
      const name = (item.title || item.a || "").toLowerCase();
      if (q && !name.includes(q)) return false;
      if (!isAsteroid && selectedFeatures.length > 0) {
        if (!selectedFeatures.includes(item.feature_type)) return false;
      }
      if (!isAsteroid && item.diameter != null) {
        const d = parseFloat(item.diameter);
        if (!isNaN(d)) {
          if (diameterMin && d < parseFloat(diameterMin)) return false;
          if (diameterMax && d > parseFloat(diameterMax)) return false;
        }
      }
      return true;
    });
  }, [data, searchText, selectedFeatures, diameterMin, diameterMax, isAsteroid]);

  // Client-side paging over the full filtered set.
  const displayRows = useMemo(() => {
    if (!filterData) return null;
    const start = (page - 1) * limit;
    return filterData.slice(start, start + limit);
  }, [filterData, page, limit]);

  const filteredCount = filterData ? filterData.length : 0;
  const totalCount = data ? data.length : 0;
  const pageCount = Math.max(1, Math.ceil(filteredCount / limit));

  const hasActiveFilters = selectedFeatures.length > 0 || diameterMin || diameterMax;

  // Reset to page 1 whenever the filtered result set changes.
  useEffect(() => {
    setPage(1);
  }, [searchText, selectedFeatures, diameterMin, diameterMax]);

  const gotoFirstPage = () => setPage(1);
  const handlePageForward = () => setPage((p) => Math.min(p + 1, pageCount));
  const handlePageBackward = () => setPage((p) => Math.max(p - 1, 1));
  const gotoLastPage = () => setPage(pageCount);

  const clearAllFilters = () => {
    setSelectedFeatures([]);
    setDiameterMin("");
    setDiameterMax("");
    setSearchText("");
  };

  const toggleFeature = (feat) => {
    setSelectedFeatures((prev) =>
      prev.includes(feat) ? prev.filter((f) => f !== feat) : [...prev, feat]
    );
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const planetHeaders = [
    { label: "" }, { label: "Region Name" }, { label: "Region Map" },
    { label: "Diameter (KM)" }, { label: "Latitude" }, { label: "Longitude" }, { label: "Feature" },
  ];
  const asteroidHeaders = [
    { label: "" }, { label: "Name" }, { label: "ad" },
    { label: "Condition" }, { label: "e" }, { label: "h" }, { label: "i" },
  ];
  const headers = isAsteroid ? asteroidHeaders : planetHeaders;

  return (
    <>
      <div className="fixed inset-0 pointer-events-none -z-10" style={{ background: "radial-gradient(ellipse at 50% 0%, #0d1f3c 0%, #09080D 60%)" }} />

      {/* Hero — full width */}
      <section className="text-white w-full px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-6">
        <div className="text-center mb-5">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sky-300 via-blue-400 to-blue-300 bg-clip-text text-transparent font-head mb-2" style={{ textTransform: "capitalize" }}>
            {staticData?.title} Land Registry
          </h1>
          <p className="text-gray-400 text-base sm:text-lg">{staticData?.count} &bull; {staticData?.category}</p>
        </div>

        {/* Side-by-side: Mint card (left) + 3D Viewer (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Mint Card — left */}
          <div className="mint-section flex">
            <div className="w-full p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-gray-900/40 to-black/40 backdrop-blur-xl border border-blue-800/20 shadow-xl text-center flex flex-col justify-center">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">Mint {staticData?.title} Regional Land Deed</h3>
              <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-4">
                <span>Price:</span>
                <Image src="/images/eth.png" alt="ETH" width={16} height={18} />
                <span className="text-sky-300 font-bold text-base">{staticData?.price}</span>
                <span>ETH</span>
              </div>
              <div className="flex items-center justify-center gap-6 mb-5">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{Number(totalClaimedNFTs || 0)}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Minted</div>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{totalCount || 0}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Total</div>
                </div>
              </div>
              <div className="w-full h-1.5 bg-white/5 rounded-full mb-5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-sky-500 to-blue-500 rounded-full transition-all duration-500" style={{ width: `${totalCount ? Math.min((Number(totalClaimedNFTs || 0) / totalCount) * 100, 100) : 0}%` }} />
              </div>
              <div className="modelButton">
                <TransactionButton
                  style={{ opacity: "1" }}
                  transaction={() => claimTo({ contract: id === "moon" ? NFTDropContractMoon : NFTDropContractMars, to: account?.address, quantity: 1n })}
                  onTransactionConfirmed={async () => {
                    setShowMintModal(true);
                    setIsFetchingMint(true);
                    try {
                      const owned = await getOwnedNFTs({
                        contract: id === "moon" ? NFTDropContractMoon : NFTDropContractMars,
                        owner: account?.address,
                      });
                      if (owned.length > 0) {
                        const latest = owned.reduce((max, nft) =>
                          nft.id > max.id ? nft : max
                        );
                        setMintedNFT(latest);
                      }
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setIsFetchingMint(false);
                    }
                  }}
                  onError={(e) => {
                    const msg = e?.message ?? "";
                    if (!msg || /user rejected|user denied|cancel/i.test(msg)) {
                      toast.info("Transaction cancelled");
                    } else {
                      toast.error(msg || "Transaction failed");
                    }
                  }}
                >
                  Claim NFT
                </TransactionButton>
              </div>
            </div>
          </div>

          {/* 3D Viewer — right */}
          <div className="relative rounded-2xl overflow-hidden border border-blue-800/20 bg-gradient-to-b from-gray-900/30 to-black/30 backdrop-blur-xl shadow-2xl shadow-blue-900/10 min-h-[460px] lg:min-h-[460px]">
            {staticData && (
              <iframe
                src={staticData.glb}
                title={`${staticData.title} 3D Model`}
                scrolling="no"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "calc(100% + 18px)",
                  height: "calc(100% + 18px)",
                  border: "0",
                  display: "block",
                }}
              />
            )}
            <img src={scroll2.src} onClick={() => { try { document.querySelector(".mint-section")?.scrollIntoView({ behavior: "smooth" }); } catch (e) {} }} className="absolute top-2 right-2 z-10 cursor-pointer opacity-70 hover:opacity-100 transition-opacity" width={80} height={20} alt="Scroll" />
          </div>
        </div>
      </section>

      {/* ── Data Table Section — full width ───────────────────────── */}
      <section className="text-white w-full px-4 sm:px-6 lg:px-10 pb-20">
        {/* Controls Bar */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 rounded-full bg-gradient-to-b from-blue-400 to-blue-500" />
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-sky-300 via-blue-400 to-blue-300 bg-clip-text text-transparent">
              {isAsteroid ? "Asteroid" : "Region"} Registry
            </h2>
            {data && (
              <span className="text-xs text-gray-500 bg-white/5 px-2.5 py-1 rounded-full tabular-nums">
                {totalCount.toLocaleString()} total
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            {/* Search */}
            <div className="relative flex-1 lg:flex-none lg:w-64">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                className="input-primary !pl-10 !py-2 !text-sm !rounded-xl w-full"
                placeholder={`Search ${isAsteroid ? "asteroids" : "regions"}...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* Per-page selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">Per page:</span>
              <div className="flex rounded-lg border border-white/10 overflow-hidden">
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleLimitChange(size)}
                    className={`px-2.5 py-1.5 text-xs font-medium transition-all duration-200 ${
                      limit === size
                        ? "bg-gradient-to-r from-sky-500 to-blue-500 text-white"
                        : "bg-white/[0.03] text-gray-400 hover:bg-white/[0.06] hover:text-white"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section — only for non-asteroids */}
        {!isAsteroid && (
          <div className="mb-4 space-y-3">
            {/* Feature type filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 mr-1">Features:</span>
              {availableFeatures.map((feat) => (
                <button
                  key={feat}
                  onClick={() => toggleFeature(feat)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${
                    selectedFeatures.includes(feat)
                      ? "bg-sky-500/20 border-sky-500/40 text-sky-200"
                      : "bg-white/[0.03] border-white/10 text-gray-400 hover:border-white/20 hover:text-gray-300"
                  }`}
                >
                  {feat}
                </button>
              ))}

              {/* Diameter range */}
              <span className="text-xs text-gray-500 ml-2 mr-1">Diameter:</span>
              <input
                type="number"
                placeholder="Min"
                value={diameterMin}
                onChange={(e) => setDiameterMin(e.target.value)}
                className="w-20 px-2 py-1 text-xs rounded-lg bg-black/30 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-sky-500/50 transition-colors"
              />
              <span className="text-gray-600 text-xs">—</span>
              <input
                type="number"
                placeholder="Max"
                value={diameterMax}
                onChange={(e) => setDiameterMax(e.target.value)}
                className="w-20 px-2 py-1 text-xs rounded-lg bg-black/30 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-sky-500/50 transition-colors"
              />
            </div>

            {/* Active filter chips */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Active filters:</span>
                {selectedFeatures.map((feat) => (
                  <FilterChip key={feat} label={feat} onRemove={() => toggleFeature(feat)} />
                ))}
                {diameterMin && (
                  <FilterChip label={`Diameter ≥ ${diameterMin}`} onRemove={() => setDiameterMin("")} />
                )}
                {diameterMax && (
                  <FilterChip label={`Diameter ≤ ${diameterMax}`} onRemove={() => setDiameterMax("")} />
                )}
                <button onClick={clearAllFilters} className="text-xs text-gray-500 hover:text-red-400 transition-colors ml-1 underline underline-offset-2">
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl border border-blue-800/20 bg-gradient-to-b from-gray-900/30 to-black/30 backdrop-blur-xl overflow-hidden">
          {/* Headers */}
          <div
            className="grid items-center gap-3 px-4 py-3 border-b border-white/5 bg-white/[0.02]"
            style={{ gridTemplateColumns: "36px 1.5fr 1fr 1fr 1fr 1fr 1fr" }}
          >
            {headers.map((h, i) => (
              <div key={i} className={`text-xs font-semibold text-gray-500 uppercase tracking-wider ${i > 0 && i !== 1 ? "text-center" : ""}`}>
                {h.label}
              </div>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
              <div className="w-5 h-5 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
              Loading all regions…
            </div>
          )}

          {/* Rows */}
          {!loading && displayRows && displayRows.length > 0 && (
            <div className="p-1.5">
              {displayRows.map((item, index) => (
                <RegionRow
                  key={item.id || `row-${index}`}
                  item={item}
                  staticData={staticData}
                  isAsteroid={isAsteroid}
                />
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && displayRows && displayRows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <svg className="w-12 h-12 mb-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p>No results found</p>
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="mt-2 text-sm text-blue-400 hover:text-sky-300 underline underline-offset-2">
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {!loading && data && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-white/5 gap-3">
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span className="tabular-nums">{(displayRows?.length || 0).toLocaleString()} shown</span>
                <span className="text-xs text-gray-600">
                  ({filteredCount.toLocaleString()}
                  {hasActiveFilters ? ` of ${totalCount.toLocaleString()}` : ""} {isAsteroid ? "asteroids" : "regions"})
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={gotoFirstPage} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors">
                  <MdFirstPage size="1.25em" />
                </button>
                <button onClick={handlePageBackward} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors">
                  <MdChevronLeft size="1.25em" />
                </button>
                <span className="px-3 text-sm text-gray-300 tabular-nums">
                  Page {page} <span className="text-gray-500">of</span> {pageCount}
                </span>
                <button onClick={handlePageForward} disabled={page >= pageCount} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors">
                  <MdChevronRight size="1.25em" />
                </button>
                <button onClick={gotoLastPage} disabled={page >= pageCount} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors">
                  <MdLastPage size="1.25em" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Mint Success Modal ─────────────────────────────────────────────── */}
      {showMintModal && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-lg flex items-center justify-center p-4"
          onClick={() => { setShowMintModal(false); setMintedNFT(null); }}
        >
          <div
            className="relative max-w-sm w-full bg-gradient-to-b from-gray-900/95 to-black/95 rounded-3xl border border-blue-800/30 p-6 shadow-2xl shadow-sky-500/20 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {isFetchingMint ? (
              <>
                <div className="w-16 h-16 rounded-full bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                </div>
                <h2 className="text-white text-xl font-bold mb-2">Minting Successful!</h2>
                <p className="text-gray-400 text-sm">Fetching your new NFT...</p>
              </>
            ) : mintedNFT ? (
              <>
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-white text-2xl font-bold mb-1">NFT Minted!</h2>
                <p className="text-gray-400 text-sm mb-5">Your new land deed is ready</p>
                <div className="rounded-2xl overflow-hidden border border-blue-800/30 mb-4 bg-black/40">
                  <MediaRenderer
                    client={client}
                    src={mintedNFT.metadata.image}
                    alt={mintedNFT.metadata.name || "NFT"}
                    style={{ width: "100%", maxHeight: "260px", objectFit: "cover" }}
                  />
                </div>
                <h3 className="text-sky-200 font-semibold text-lg mb-1">
                  {mintedNFT.metadata.name || `Token #${mintedNFT.id.toString()}`}
                </h3>
                <p className="text-gray-500 text-xs mb-6">
                  Token #{mintedNFT.id.toString()} &bull; {staticData?.title}
                </p>
                <button
                  onClick={() => router.push("/profile")}
                  className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-500 text-white rounded-2xl font-bold hover:from-blue-600 hover:to-blue-600 transition-all duration-300 shadow-xl shadow-sky-500/20 mb-3"
                >
                  View Dashboard
                </button>
                <button
                  onClick={() => { setShowMintModal(false); setMintedNFT(null); }}
                  className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-white text-2xl font-bold mb-2">NFT Minted!</h2>
                <p className="text-gray-400 text-sm mb-6">Your transaction was confirmed successfully.</p>
                <button
                  onClick={() => router.push("/profile")}
                  className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-500 text-white rounded-2xl font-bold hover:from-blue-600 hover:to-blue-600 transition-all duration-300 shadow-xl shadow-sky-500/20 mb-3"
                >
                  View Dashboard
                </button>
                <button
                  onClick={() => setShowMintModal(false)}
                  className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
